import type { Book, BookProgress, Chapter, Track } from '../types';

const hasLength = (t: Track) => typeof t.durationSec === 'number' && t.durationSec > 0;

/**
 * Every track's length, in seconds. Exact for tracks whose file has been read;
 * the rest are estimated from their file size, at the bitrate of the tracks
 * that are known. The files of one audiobook almost always share a bitrate, so
 * one measured track is enough to put the whole book within a few percent.
 * Unknowable (all zeros) until at least one track has been measured.
 */
export function trackLengths(book: Book): number[] {
  let knownBytes = 0;
  let knownSec = 0;
  for (const t of book.tracks) {
    if (hasLength(t) && t.sizeBytes > 0) {
      knownBytes += t.sizeBytes;
      knownSec += t.durationSec ?? 0;
    }
  }
  const secPerByte = knownBytes > 0 ? knownSec / knownBytes : 0;
  return book.tracks.map((t) => (hasLength(t) ? (t.durationSec ?? 0) : t.sizeBytes * secPerByte));
}

/** Length of the whole book, estimated where need be; 0 while unknowable. */
export function totalDuration(book: Book): number {
  return trackLengths(book).reduce((sum, s) => sum + s, 0);
}

/** True when every figure is measured rather than estimated — shown with a
 *  "~" otherwise. */
export function allDurationsKnown(book: Book): boolean {
  return book.tracks.every(hasLength);
}

/** Position measured from the start of the book rather than the current track. */
export function absolutePosition(book: Book, trackIndex: number, positionSec: number): number {
  const lengths = trackLengths(book);
  let sum = 0;
  for (let i = 0; i < trackIndex && i < lengths.length; i++) sum += lengths[i] ?? 0;
  return sum + positionSec;
}

/** Whether a book has been listened to at all, whether or not its length is
 *  known yet. */
export function hasStarted(entry: BookProgress | undefined): boolean {
  if (!entry) return false;
  return entry.finished || entry.trackIndex > 0 || entry.positionSec > 1;
}

/** The book you were most recently part-way through, on any device. */
export function lastStarted(
  books: Book[],
  progress: Record<string, BookProgress>,
): { book: Book; entry: BookProgress } | null {
  let best: { book: Book; entry: BookProgress } | null = null;
  for (const book of books) {
    const entry = progress[book.id];
    if (!entry || entry.finished || !hasStarted(entry)) continue;
    if (!best || Date.parse(entry.updatedAt) > Date.parse(best.entry.updatedAt)) {
      best = { book, entry };
    }
  }
  return best;
}

/** 0..1 through the whole book, or 0 while its length is unknowable. */
export function bookRatio(book: Book, entry: BookProgress | undefined): number {
  if (!entry) return 0;
  if (entry.finished) return 1;
  const total = totalDuration(book);
  if (total <= 0) return 0;
  return Math.min(1, absolutePosition(book, entry.trackIndex, entry.positionSec) / total);
}

export function remainingSeconds(book: Book, entry: BookProgress | undefined): number {
  const total = totalDuration(book);
  if (total <= 0) return 0;
  if (!entry) return total;
  if (entry.finished) return 0;
  return Math.max(0, total - absolutePosition(book, entry.trackIndex, entry.positionSec));
}

/** The chapter list a book should show. Multi-file books fall back to one
 *  entry per file, which is what the file names mean in practice. */
export function effectiveChapters(book: Book, parsed: Chapter[]): Chapter[] {
  if (parsed.length > 0) return parsed;
  return book.tracks.map((t, i) => ({ title: t.name, trackIndex: i, startSec: 0 }));
}

/** Duration of a chapter, given the chapter after it and the track lengths —
 *  estimated for files not yet played, 0 while unknowable. */
export function chapterDuration(book: Book, chapters: Chapter[], index: number): number {
  const chapter = chapters[index];
  if (!chapter) return 0;
  const next = chapters[index + 1];
  const trackDuration = trackLengths(book)[chapter.trackIndex] ?? 0;

  if (next && next.trackIndex === chapter.trackIndex) return next.startSec - chapter.startSec;
  return Math.max(0, trackDuration - chapter.startSec);
}
